import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/jianpu.css";
import { degreeToMidi, noteKey } from "../lib/jianpu";
import {
  CHORD_SHAPES,
  PROGRESSIONS,
  WORKOUT,
  getChord,
  cellLabel,
  chordMidis,
  advanceOnNote,
} from "../lib/jianpuChords";
import { playNote, resumeAudio } from "../lib/homeKeyAudio";
import { MELODY_PATCH } from "../components/jianpu/useJianpuPlayer";
import EasyPlayGrid from "../components/jianpu/EasyPlayGrid";
import useEasyPlay from "../components/jianpu/useEasyPlay";

// Audio previews use a fixed do — the real pitch comes from the transpose
// dial anyway, so C keeps demos consistent.
const PREVIEW_TONIC = 60;

// Chord Lab — the printed chord sheet, alive: every shape from the practice
// PDF on a lit-up button grid, with audio, plus collect-the-chord progression
// practice over MIDI (or on-screen taps).
const JianpuChords = () => {
  const [selectedId, setSelectedId] = useState("I");
  // practice: null | { progId, step, laps }
  const [practice, setPractice] = useState(null);
  const [accum, setAccum] = useState(() => new Set());
  const [spilled, setSpilled] = useState(false);
  const practiceRef = useRef(practice);
  practiceRef.current = practice;
  const accumRef = useRef(accum);
  accumRef.current = accum;

  const prog = practice ? PROGRESSIONS.find((p) => p.id === practice.progId) : null;
  const chord = practice ? getChord(prog.chords[practice.step]) : getChord(selectedId);

  const playChord = useCallback((shape, roll = false) => {
    resumeAudio();
    chordMidis(shape, PREVIEW_TONIC).forEach((midi, i) => {
      playNote(MELODY_PATCH, midi, roll ? 700 : 1100, roll ? i * 190 : 0, 0.9);
    });
  }, []);

  const handleInput = useCallback(
    (cell) => {
      const p = practiceRef.current;
      if (!p) return;
      const currentProg = PROGRESSIONS.find((x) => x.id === p.progId);
      const currentChord = getChord(currentProg.chords[p.step]);
      const result = advanceOnNote(accumRef.current, currentChord, noteKey(cell));
      setSpilled(result.wrong);
      if (result.complete) {
        playChord(currentChord);
        setAccum(new Set());
        setPractice((prev) => {
          const nextStep = (prev.step + 1) % currentProg.chords.length;
          return {
            ...prev,
            step: nextStep,
            laps: nextStep === 0 ? prev.laps + 1 : prev.laps,
          };
        });
      } else {
        setAccum(result.accum);
      }
    },
    [playChord]
  );

  const onNote = useCallback((played) => handleInput(played), [handleInput]);
  const ep = useEasyPlay({ onNote });

  const tapPad = (cell) => {
    // On-screen taps sound the note and count toward the chord.
    resumeAudio();
    playNote(
      MELODY_PATCH,
      degreeToMidi(PREVIEW_TONIC, cell.degree, cell.octave, cell.accidental || 0),
      380
    );
    handleInput({ accidental: 0, ...cell });
  };

  const startPractice = (progId) => {
    setPractice({ progId, step: 0, laps: 0 });
    setAccum(new Set());
    setSpilled(false);
  };

  const stopPractice = () => {
    setPractice(null);
    setAccum(new Set());
    setSpilled(false);
  };

  const page1 = CHORD_SHAPES.filter((s) => s.page === 1);
  const page2 = CHORD_SHAPES.filter((s) => s.page === 2);

  const chordCard = (shape) => (
    <button
      key={shape.id}
      type="button"
      className="jp-card jp-chord-card"
      data-selected={!practice && selectedId === shape.id ? "true" : undefined}
      onClick={() => {
        stopPractice();
        setSelectedId(shape.id);
        playChord(shape);
      }}
    >
      <span className="jp-card-title">{shape.numeral}</span>
      <span className="jp-card-meta">
        {shape.cells.map(cellLabel).join(" · ")}
      </span>
      <span className="jp-card-meta" style={{ fontSize: "0.75rem" }}>
        {shape.blurb}
      </span>
    </button>
  );

  return (
    <div className="jp-root">
      <div className="jp-shell">
        <div className="jp-btn-row" style={{ justifyContent: "space-between" }}>
          <Link to="/jianpu" className="jp-back">
            ← jianpu studio
          </Link>
          <span className="jp-kicker">chord lab</span>
        </div>

        <header style={{ marginTop: "1.2rem", maxWidth: "38rem" }}>
          <h1 style={{ fontSize: "1.9rem" }}>Chord Lab</h1>
          <p style={{ marginTop: "0.5rem", color: "var(--jp-ink-dim)" }}>
            The master pattern: any every-other-button line, reading order,
            wrapping rows, is a chord — and the dial means one fingering works
            in all twelve keys. Tap a shape to see and hear it, then loop the
            progressions until your hands know them.
          </p>
        </header>

        <div className="jp-play-grid">
          <div>
            <div className="jp-panel">
              <div
                className="jp-btn-row"
                style={{ justifyContent: "space-between", marginBottom: "0.9rem" }}
              >
                <div>
                  <span className="jp-label">
                    {practice ? `${prog.name} — play` : "showing"}
                  </span>
                  <h2 style={{ fontSize: "1.5rem" }}>
                    {chord.numeral}{" "}
                    <span style={{ fontSize: "0.95rem", color: "var(--jp-ink-dim)" }}>
                      {chord.name} · {chord.cells.map(cellLabel).join(" ")}
                    </span>
                  </h2>
                </div>
                <div className="jp-btn-row">
                  <button type="button" className="jp-btn" onClick={() => playChord(chord)}>
                    hear it
                  </button>
                  <button type="button" className="jp-btn" onClick={() => playChord(chord, true)}>
                    roll it
                  </button>
                </div>
              </div>

              <EasyPlayGrid
                targets={chord.cells}
                pressed={practice ? accum : ep.pressed}
                onPress={tapPad}
              />

              {practice ? (
                <div style={{ marginTop: "0.9rem", textAlign: "center" }}>
                  <p style={{ fontSize: "0.9rem" }}>
                    {prog.chords.map((id, i) => (
                      <span
                        key={i}
                        style={{
                          margin: "0 0.4rem",
                          fontWeight: i === practice.step ? 700 : 400,
                          color:
                            i === practice.step ? "var(--jp-red)" : "var(--jp-ink-dim)",
                        }}
                      >
                        {getChord(id).numeral}
                      </span>
                    ))}
                  </p>
                  <p style={{ fontSize: "0.82rem", color: "var(--jp-ink-dim)", marginTop: "0.3rem" }}>
                    {spilled
                      ? "stray note — chord starts over"
                      : `collect all ${chord.cells.length} tones, any order — press together or roll them`}
                    {practice.laps > 0 && ` · laps: ${practice.laps}`}
                  </p>
                  <button
                    type="button"
                    className="jp-btn"
                    style={{ marginTop: "0.6rem" }}
                    onClick={stopPractice}
                  >
                    ■ stop practicing
                  </button>
                </div>
              ) : (
                <p
                  style={{
                    marginTop: "0.9rem",
                    fontSize: "0.82rem",
                    color: "var(--jp-ink-dim)",
                    textAlign: "center",
                  }}
                >
                  tip: roll every shape low-to-high as an arpeggio, not just
                  pressed together
                </p>
              )}
            </div>

            <section style={{ marginTop: "1.5rem" }}>
              <h2 style={{ fontSize: "1.15rem" }}>On the big buttons</h2>
              <div className="jp-card-grid" style={{ marginTop: "0.8rem" }}>
                {page1.map(chordCard)}
              </div>
              <h2 style={{ fontSize: "1.15rem", marginTop: "1.5rem" }}>
                Borrowed colors <span style={{ fontSize: "0.85rem", color: "var(--jp-ink-dim)" }}>— reaching for the small gray buttons</span>
              </h2>
              <div className="jp-card-grid" style={{ marginTop: "0.8rem" }}>
                {page2.map(chordCard)}
              </div>
            </section>
          </div>

          <aside className="jp-side">
            {!ep.supported && (
              <div className="jp-banner">
                No Web MIDI in this browser (Chrome/Edge have it) — the
                on-screen buttons still work for everything here.
              </div>
            )}

            <div className="jp-panel">
              <div className="jp-label" style={{ marginBottom: "0.5rem" }}>
                progressions to practice
              </div>
              <div className="jp-saved-list">
                {PROGRESSIONS.map((p) => (
                  <div key={p.id} className="jp-saved-row">
                    <span>
                      <strong style={{ fontWeight: 600 }}>{p.name}</strong>
                      <br />
                      <span style={{ fontSize: "0.78rem", color: "var(--jp-ink-dim)" }}>
                        {p.chords.map((id) => getChord(id).numeral).join(" – ")}
                      </span>
                    </span>
                    <button type="button" onClick={() => startPractice(p.id)}>
                      {practice?.progId === p.id ? "restart" : "practice"}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="jp-panel">
              <div className="jp-label" style={{ marginBottom: "0.5rem" }}>
                five-minute workout
              </div>
              <ol style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.82rem", display: "grid", gap: "0.45rem" }}>
                {WORKOUT.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ol>
              <p style={{ fontSize: "0.75rem", color: "var(--jp-ink-dim)", marginTop: "0.6rem" }}>
                Remember: b3 lives on the ♯2 button, b6 on ♯5, b7 on ♯6 — a
                flat is the sharp of the note below it.
              </p>
            </div>

            <div className="jp-panel">
              <div className="jp-label">instrument</div>
              <p style={{ fontSize: "0.85rem", marginTop: "0.4rem" }}>
                {ep.connected ? `connected: ${ep.deviceNames.join(", ")}` : "nothing connected"}
              </p>
              {ep.calibrating ? (
                <p style={{ fontSize: "0.85rem", color: "var(--jp-red)", marginTop: "0.4rem" }}>
                  …press your ① button
                </p>
              ) : (
                <button
                  type="button"
                  className="jp-btn"
                  style={{ marginTop: "0.5rem" }}
                  onClick={ep.startCalibration}
                  disabled={!ep.connected}
                >
                  calibrate — press ①
                </button>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default JianpuChords;
